import { trace } from '@opentelemetry/api';
import {
    WebTracerProvider,
    BatchSpanProcessor
} from '@opentelemetry/sdk-trace-web';
import { Resource } from '@opentelemetry/resources';
import {
    SEMRESATTRS_SERVICE_NAME
} from '@opentelemetry/semantic-conventions';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';

export function register() {
    const tracesEnabled = process.env.NEXT_PUBLIC_OTEL_TRACES_ENABLED === 'true';

    if (!tracesEnabled) {
        console.log('[OTEL] Client-side OpenTelemetry tracing is disabled');
        return;
    }

    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
        console.log('[OTEL] Client-side initialization skipped (not in browser)');
        return;
    }

    try {
        const otlpEndpoint = process.env.NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT || 'http://localhost:4318';

        // Parse headers - exact pattern from backend
        const headers: Record<string, string> = {};
        const otlpHeaders = process.env.NEXT_PUBLIC_OTEL_EXPORTER_OTLP_HEADERS || '';
        otlpHeaders.split(',').forEach((header: string) => {
            const parts = header.split('=');
            if (parts.length >= 2) {
                const key = parts[0].trim();
                // Use slice to handle values with equals signs
                const value = parts.slice(1).join('=').trim();
                if (key && value) {
                    headers[key] = value;
                }
            }
        });

        // Parse resource attributes - exact pattern from backend
        const resourceAttributes: Record<string, string> = {};
        const rawAttributes = process.env.NEXT_PUBLIC_OTEL_RESOURCE_ATTRIBUTES || '';
        rawAttributes.split(',').forEach((attr: string) => {
            const [key, value] = attr.split('=');
            if (key && value) {
                resourceAttributes[key.trim()] = value.trim();
            }
        });

        // Create resource with attributes
        const resource = new Resource({
            [SEMRESATTRS_SERVICE_NAME]: process.env.NEXT_PUBLIC_OTEL_SERVICE_NAME || 'frontend',
            'deployment.environment': process.env.NEXT_PUBLIC_ENVIRONMENT || 'development',
            ...resourceAttributes
        });

        // Create tracer provider
        const provider = new WebTracerProvider({
            resource
        });

        // Configure OTLP trace exporter
        const exporter = new OTLPTraceExporter({
            url: `${otlpEndpoint}/v1/traces`,
            headers
        });

        // Add batch span processor for better performance
        provider.addSpanProcessor(new BatchSpanProcessor(exporter));

        // Register with the global API
        trace.setGlobalTracerProvider(provider);

        // Register instrumentations for browser
        registerInstrumentations({
            instrumentations: [
                new DocumentLoadInstrumentation(),
                new FetchInstrumentation({
                    propagateTraceHeaderCorsUrls: [/.*/],
                    ignoreUrls: [/localhost:4000/, /vercel\.com/]
                }),
                new UserInteractionInstrumentation()
            ],
            tracerProvider: provider
        });

        console.log('[OTEL] Client-side OpenTelemetry initialized successfully');
    } catch (error) {
        console.error('[OTEL] Failed to initialize client-side OpenTelemetry:', error);
        // Don't crash the app if OTEL fails
    }
}
